// frontend/src/components/inventaire/SupplierLinksPanel.tsx
// Full-screen modal listing every supplier-resource link with bulk
// import/export controls. Uses the preview-then-expand pattern from the
// recettes page (8 visible by default, "Voir tout" reveals the rest).

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  X,
  ArrowLeftRight,
  Search,
  Truck,
  Package,
  Star,
  ChevronDown,
  ChevronUp,
  DollarSign,
} from "lucide-react";
import { IconButton, Button } from "../ui/button";
import {
  fetchSupplierResourceLinks,
  type SupplierResourceLink,
} from "../../services/supplierResourceService";
import { SupplierResourceImportExport } from "../import-export/SupplierResourceImportExport";

const PREVIEW_COUNT = 8;

interface SupplierLinksPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export function SupplierLinksPanel({ isOpen, onClose }: SupplierLinksPanelProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [supplierFilter, setSupplierFilter] = useState<string>("all");
  const [expanded, setExpanded] = useState(false);
  const [isImportExportOpen, setIsImportExportOpen] = useState(false);

  const { data: links = [], isLoading } = useQuery({
    queryKey: ["supplier-resource-links"],
    queryFn: fetchSupplierResourceLinks,
    enabled: isOpen,
  });

  const supplierBuckets = useMemo(() => {
    const map = new Map<string, number>();
    for (const l of links) {
      map.set(l.supplier.name, (map.get(l.supplier.name) ?? 0) + 1);
    }
    return [...map.entries()].sort((a, b) => b[1] - a[1]);
  }, [links]);

  const filtered = useMemo(() => {
    return links.filter((l) => {
      if (supplierFilter !== "all" && l.supplier.name !== supplierFilter) return false;
      if (searchTerm) {
        const t = searchTerm.toLowerCase();
        const inSup = l.supplier.name.toLowerCase().includes(t);
        const inRes = l.resource.name.toLowerCase().includes(t);
        const inSku = (l.link.supplierSku ?? "").toLowerCase().includes(t);
        if (!inSup && !inRes && !inSku) return false;
      }
      return true;
    });
  }, [links, supplierFilter, searchTerm]);

  const visibleLinks = expanded ? filtered : filtered.slice(0, PREVIEW_COUNT);
  const hasMore = filtered.length > PREVIEW_COUNT;

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
        <div
          className="bg-[var(--card)] rounded-xl shadow-xl border border-[var(--border)] w-full animate-in fade-in zoom-in-95 duration-200 flex flex-col"
          style={{ maxWidth: "64rem", maxHeight: "90vh" }}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-[var(--border)] flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[var(--color-steel-bg)]">
                <Truck className="w-5 h-5 text-[var(--color-steel)]" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-[var(--foreground)]">
                  Liens fournisseurs ↔ ressources
                </h2>
                <p className="text-xs text-[var(--muted-foreground)] mt-0.5">
                  {links.length} lien{links.length > 1 ? "s" : ""} au total
                  {supplierBuckets.length > 0 && (
                    <> · {supplierBuckets.length} fournisseur{supplierBuckets.length > 1 ? "s" : ""}</>
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                icon={ArrowLeftRight}
                onClick={() => setIsImportExportOpen(true)}
              >
                Importer / Exporter
              </Button>
              <IconButton icon={X} variant="ghost" onClick={onClose} aria-label="Fermer" />
            </div>
          </div>

          {/* Filters */}
          <div className="flex items-center gap-2 px-5 py-3 border-b border-[var(--border)] flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[var(--muted-foreground)]" />
              <input
                type="text"
                placeholder="Rechercher fournisseur, ressource ou SKU..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 text-xs bg-[var(--input-background)] border border-[var(--switch-background)] rounded-md focus:border-[var(--color-steel)] focus:ring-1 focus:ring-[var(--color-steel-bg)] text-[var(--foreground)]"
              />
            </div>
            {supplierBuckets.length > 1 && (
              <select
                value={supplierFilter}
                onChange={(e) => {
                  setSupplierFilter(e.target.value);
                  setExpanded(false);
                }}
                className="px-2 py-1.5 text-xs bg-[var(--input-background)] border border-[var(--switch-background)] rounded-md focus:border-[var(--color-steel)] focus:ring-1 focus:ring-[var(--color-steel-bg)] text-[var(--foreground)]"
              >
                <option value="all">Tous les fournisseurs</option>
                {supplierBuckets.map(([name, count]) => (
                  <option key={name} value={name}>
                    {name} ({count})
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto px-5 py-3">
            {isLoading ? (
              <div className="py-10 text-center text-sm text-[var(--muted-foreground)]">
                Chargement...
              </div>
            ) : filtered.length === 0 ? (
              <div className="py-10 text-center">
                <Truck className="w-12 h-12 mx-auto text-[var(--muted-foreground)] opacity-30 mb-3" />
                <h3 className="text-base font-semibold text-[var(--foreground)] mb-1">
                  {links.length === 0 ? "Aucun lien fournisseur encore" : "Aucun résultat"}
                </h3>
                <p className="text-sm text-[var(--muted-foreground)] max-w-md mx-auto">
                  {links.length === 0
                    ? "Importez un fichier JSON pour lier les ressources à leurs fournisseurs en lot."
                    : "Essayez d'ajuster les filtres ou la recherche."}
                </p>
              </div>
            ) : (
              <>
                <div className="space-y-1.5">
                  {visibleLinks.map((l, i) => (
                    <SupplierLinkRow key={`${l.supplier.name}|${l.resource.name}|${i}`} link={l} />
                  ))}
                </div>
                {hasMore && (
                  <div className="flex justify-center pt-3">
                    <button
                      onClick={() => setExpanded((s) => !s)}
                      className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-md border border-[var(--border)] text-[var(--muted-foreground)] hover:border-[var(--color-steel)] hover:text-[var(--color-steel)] hover:bg-[var(--muted)] transition-colors"
                    >
                      {expanded ? (
                        <>
                          <ChevronUp className="w-4 h-4" />
                          Réduire
                        </>
                      ) : (
                        <>
                          <ChevronDown className="w-4 h-4" />
                          Voir tout ({filtered.length})
                        </>
                      )}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <SupplierResourceImportExport
        isOpen={isImportExportOpen}
        onClose={() => setIsImportExportOpen(false)}
      />
    </>
  );
}

// ─── Row component ───────────────────────────────────────────────────────────

interface SupplierLinkRowProps {
  link: SupplierResourceLink;
}

function SupplierLinkRow({ link }: SupplierLinkRowProps) {
  const cost = link.link.unitCost > 0 ? `${link.link.unitCost.toFixed(2)} $` : "—";
  const moq = link.link.minOrderQty != null && link.link.minOrderQty > 0 ? link.link.minOrderQty : null;
  const lead = link.link.leadTimeDays != null && link.link.leadTimeDays > 0 ? link.link.leadTimeDays : null;

  return (
    <div className="flex items-center gap-3 px-3 py-2 rounded-lg border border-[var(--border)] bg-[var(--card)] hover:border-[var(--color-steel)]/40 transition-colors">
      <Package className="w-4 h-4 text-[var(--muted-foreground)] flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-[var(--foreground)] truncate">
          {link.resource.name}
        </div>
        <div className="text-xs text-[var(--muted-foreground)] truncate flex items-center gap-1">
          <Truck className="w-3 h-3" />
          <span className="truncate">{link.supplier.name}</span>
          {link.supplier.code && (
            <span className="text-[10px] opacity-75">({link.supplier.code})</span>
          )}
        </div>
      </div>
      <div className="hidden sm:flex items-center gap-3 text-xs flex-shrink-0">
        <span
          className="inline-flex items-center gap-1 text-[var(--color-olive)]"
          title="Coût unitaire"
        >
          <DollarSign className="w-3.5 h-3.5" strokeWidth={2.25} />
          <span className="tabular-nums">{cost}</span>
        </span>
        {moq != null && (
          <span
            className="inline-flex items-center gap-1 text-[var(--color-steel)]"
            title="Quantité minimale de commande"
          >
            <Package className="w-3.5 h-3.5" strokeWidth={2.25} />
            <span className="tabular-nums">MOQ {moq}</span>
          </span>
        )}
        {lead != null && (
          <span
            className="inline-flex items-center gap-1 text-[var(--color-amber)]"
            title="Délai de livraison"
          >
            <Truck className="w-3.5 h-3.5" strokeWidth={2.25} />
            <span className="tabular-nums">{lead} j</span>
          </span>
        )}
        {link.link.isPreferred && (
          <span
            className="inline-flex items-center gap-1 text-[var(--color-amber)]"
            title="Fournisseur préféré pour cette ressource"
          >
            <Star className="w-3.5 h-3.5 fill-current" strokeWidth={2.25} />
          </span>
        )}
      </div>
    </div>
  );
}
